import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { MessageSquare, Inbox } from 'lucide-react';
import { Header } from '../components/Header';
import { ChatListItem } from '../components/ChatListItem';
import { ChatConversationView } from '../components/ChatConversationView';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { Chat } from '../types';
import { getUserChats, getProviderChats } from '../services/api';
import { ROUTES } from '../constants';

export const MessagesPage: React.FC = () => {
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userRole = user?.role === 'provider' ? 'provider' : 'user';
  const selectedChat = chats.find((chat) => chat.chatId === selectedChatId);

  const fetchChats = async () => {
    setLoading(true);
    setError(null);

    try {
      const response =
        userRole === 'provider'
          ? await getProviderChats()
          : await getUserChats();

      if (response.success) {
        const sortedChats = response.chats.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        setChats(sortedChats);

        if (sortedChats.length > 0 && !selectedChatId) {
          setSelectedChatId(sortedChats[0].chatId);
        }
      } else {
        setError(response.error || 'Failed to load chats');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChats();
  }, [userRole]);

  const handleMessageSent = () => {
    fetchChats();
  };

  const theme = userRole === 'provider' ? 'green' : 'blue';

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Header theme={theme} />

      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 overflow-hidden">
        <div className="mb-4 sm:mb-6 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`p-2 sm:p-3 rounded-lg ${
              userRole === 'provider' ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              <MessageSquare className={`h-5 w-5 sm:h-6 sm:w-6 ${
                userRole === 'provider' ? 'text-green-600' : 'text-blue-600'
              }`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Messages</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-0.5 sm:mt-1 hidden sm:block">
                {userRole === 'provider'
                  ? 'View and respond to messages from service users'
                  : 'View messages from care providers'}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
            <span className="ml-3 text-gray-600">Loading messages...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        ) : chats.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
            <div className={`inline-flex p-3 sm:p-4 rounded-full ${
              userRole === 'provider' ? 'bg-green-100' : 'bg-blue-100'
            } mb-4`}>
              <Inbox className={`h-8 w-8 sm:h-12 sm:w-12 ${
                userRole === 'provider' ? 'text-green-600' : 'text-blue-600'
              }`} />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No messages yet</h3>
            <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto">
              {userRole === 'provider'
                ? 'When you contact service users about their care requests, your conversations will appear here.'
                : 'When care providers reach out about your care requests, their messages will appear here.'}
            </p>
          </div>
        ) : (
          <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col min-h-0">
            <div className="flex flex-col md:flex-row flex-1 min-h-0">
              <div className="md:w-80 lg:w-96 border-r border-gray-200 flex flex-col h-full">
                <div className="bg-gray-50 border-b border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 flex-shrink-0">
                  <h2 className="font-semibold text-gray-900 text-sm sm:text-base">
                    Conversations ({chats.length})
                  </h2>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0">
                  {chats.map((chat) => (
                    <ChatListItem
                      key={chat.chatId}
                      chat={chat}
                      isSelected={chat.chatId === selectedChatId}
                      onClick={() => setSelectedChatId(chat.chatId)}
                      userRole={userRole}
                    />
                  ))}
                </div>
              </div>

              <div className="flex-1 flex flex-col min-h-0">
                {selectedChat ? (
                  <ChatConversationView
                    chat={selectedChat}
                    userRole={userRole}
                    onMessageSent={handleMessageSent}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-sm sm:text-base">Select a conversation to view messages</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
